/* eslint-disable import/order */
import React from 'react';
import { MenuIdSettings } from '@jira/navigation-apps__constants/src/MenuIdSettings.tsx';
import { SidebarIdGlobalProductsSettings } from '@jira/navigation-apps__constants/src/SidebarIdGlobalProductsSettings.tsx';
import type CustomerAccessType from '@jira/spa-apps__admin-pages-customer-access/src/CustomerAccessType.tsx';
import {
	jsmGlobalCustomerAccessAffectedProjectsResource,
	jsmFilteredAffectedProjectsResource,
} from '@jira/router-resources__admin-pages-jsm-customer-access/src/jsmGlobalCustomerAccessAffectedProjectsResource.tsx';
import { jsmGlobalCustomerAccessSettingsResource } from '@jira/router-resources__admin-pages-jsm-customer-access/src/jsmGlobalCustomerAccessSettingsResource.tsx';
import { LazyAtlassianNavigation } from '@jira/navigation-apps__atlassian-navigation/src/async.tsx';
import { globalSettingsLayout } from '@jira/platform__layouts-global-settings-layout/src/index.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import {
	getNavigationResources,
	getNavigationSidebarProductsSettingResource,
} from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { productsJsmCustomerAccessRoute } from '@jira/router-routes__admin-pages-jsm-customer-access-routes/src/productsJsmCustomerAccessRoute.tsx';
import { markAsProductBundleReady } from '@jira/platform__spa/src/services/mark-as-product-bundle-ready/index.tsx';
import { LazyPage } from '@jira/platform__spa/src/view/lazy-page/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import type { Route } from '@atlassian/react-resource-router';

const LazyCustomerAccess = lazyForPaint<typeof CustomerAccessType>(() =>
	markAsProductBundleReady(
		import(
			/* webpackChunkName: "async-products-customer-access", jiraSpaEntry: "async-admin-pages-customer-access" */ '@jira/spa-apps__admin-pages-customer-access/src/CustomerAccessType.tsx'
		),
	),
);

const ServiceDeskCustomerAccess = () => (
	<LazyPage
		Page={LazyCustomerAccess}
		pageId="spa-apps/admin-pages/customer-access"
		shouldShowSpinner={false}
	/>
);

export const productsJsmCustomerAccessRouteEntry: Route = createEntry(
	productsJsmCustomerAccessRoute,
	() => ({
		group: 'global-settings',
		ufoName: 'admin.jsm-customer-access',
		// eslint-disable-next-line no-restricted-syntax
		component: ServiceDeskCustomerAccess,
		layout: globalSettingsLayout,

		navigation: {
			sidebarId: SidebarIdGlobalProductsSettings,
			menuId: MenuIdSettings,
		},

		resources: [
			...getNavigationResources(),
			...getNavigationSidebarProductsSettingResource(),
			jsmGlobalCustomerAccessSettingsResource,
			resourceWithCondition(
				() => !fg('csm_customer_access_jsm_separation'),
				jsmGlobalCustomerAccessAffectedProjectsResource,
			),
			resourceWithCondition(
				() => fg('csm_customer_access_jsm_separation'),
				jsmFilteredAffectedProjectsResource,
			),
		],

		forPaint: [LazyAtlassianNavigation, LazyCustomerAccess],

		bifrost: {
			bifrostGroup: 'shell-only',
		},
	}),
);
